/**
 * ProfileCompletenessBanner
 * Surfaces on portal dashboards. Shows a red-tinted banner whenever the
 * profile is < 100% complete with a list of missing fields and a CTA.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle2, ChevronRight, ChevronDown, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ProfileCompletenessBanner = ({ getToken, onComplete }) => {
  const [dismissed, setDismissed] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['profile-completeness'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/school/portal/profile/completeness`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error('Failed to load');
      return res.json();
    },
    staleTime: 60_000,
  });

  if (isLoading || !data || data.is_complete || dismissed) return null;

  const missingRequired = (data.summary || []).filter(s => s.severity === 'required' && !s.present);
  const missingImportant = (data.summary || []).filter(s => s.severity === 'important' && !s.present);
  const score = data.score;
  const isRed = missingRequired.length > 0 || score < 60;

  return (
    <Card className={`p-4 mb-4 border-l-4 ${isRed ? 'border-l-red-500 bg-red-50/50' : 'border-l-amber-500 bg-amber-50/50'}`} data-testid="profile-completeness-banner">
      <div className="flex items-start gap-3">
        <div className={`shrink-0 mt-0.5 ${isRed ? 'text-red-500' : 'text-amber-500'}`}>
          <AlertTriangle className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-800" data-testid="profile-score-heading">
                Your profile is <span className={isRed ? 'text-red-600' : 'text-amber-600'} data-testid="profile-score">{score}%</span> complete
              </h3>
              {missingRequired.length > 0 && (
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                  {missingRequired.length} required missing
                </Badge>
              )}
            </div>
            <Button
              variant="ghost" size="sm" className="h-7 w-7 p-0"
              onClick={() => setDismissed(true)}
              title="Dismiss (we'll remind you in a week)"
              data-testid="profile-banner-dismiss"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-sm text-slate-600 mt-1">
            Please complete the highlighted fields below — required fields are marked in red.
          </p>

          {/* Progress bar */}
          <div className="mt-3 h-2 bg-slate-200 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${isRed ? 'bg-red-500' : 'bg-amber-500'}`}
              style={{ width: `${score}%` }}
            />
          </div>

          {/* Missing list */}
          <div className="mt-3 flex flex-wrap gap-2">
            {missingRequired.slice(0, showAll ? 999 : 6).map(item => (
              <span
                key={item.key}
                className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-red-100 text-red-700 border border-red-200"
                data-testid={`profile-missing-${item.key}`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> {item.label}
              </span>
            ))}
            {missingImportant.slice(0, showAll ? 999 : Math.max(0, 6 - missingRequired.length)).map(item => (
              <span
                key={item.key}
                className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200"
                data-testid={`profile-missing-${item.key}`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> {item.label}
              </span>
            ))}
            {(missingRequired.length + missingImportant.length > 6 && !showAll) && (
              <button
                onClick={() => setShowAll(true)}
                className="text-xs text-slate-500 hover:text-slate-800 underline"
                data-testid="profile-show-all"
              >
                +{missingRequired.length + missingImportant.length - 6} more
              </button>
            )}
          </div>

          <div className="mt-3 flex items-center gap-2">
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => onComplete && onComplete()}
              data-testid="profile-complete-btn"
            >
              Complete my profile <ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Button>
            {data.summary && (
              <button
                onClick={() => setShowAll(s => !s)}
                className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
              >
                {showAll ? 'Show less' : 'See all'} {showAll ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              </button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default ProfileCompletenessBanner;
